import { cn } from "@/lib/utils";
import { PlayStyle } from "@/pages/DetailedPlayer/sections/PlayStyles/Shared";
import { contentLabelsSelector } from "@/redux/loadedContent/loadedContentSlice";
import { BREAKPOINTS } from "@/settings";
import { Trans, useTranslation } from "@repo/shared/i18n";
import React from "react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

interface MetaRatingCalculationsProps {
  topPlayStylesPlus: number[];
  topStats: string[];
  topAccelerates: string[];
}

const MetaRatingCalculations: React.FC<MetaRatingCalculationsProps> = ({
  topPlayStylesPlus,
  topStats,
  topAccelerates,
}) => {
  const { t } = useTranslation("metaRating");
  const isTouch = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const { playerAttributes } = useSelector(contentLabelsSelector);

  const accelerateLabel = (accelerate: string) =>
    accelerate === "explosive" ||
    accelerate === "controlled" ||
    accelerate === "lengthy"
      ? t(`players:filters.accelerates.${accelerate}`)
      : snakeCaseToNormalCase(accelerate);

  const snakeCaseToNormalCase = (stat: string) => {
    return stat.split(" ").map((s) => {
      // Convert snake_case to normal language
      const formattedStat = s
        .split(/(?=[A-Z])/) // Split on capital letters
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" ");
      return <label key={s}>{formattedStat}</label>;
    });
  };

  return (
    <div className={cn("flex", isTouch ? "flex-col gap-4 py-4" : "gap-4")}>
      <div
        className={cn(
          "flex flex-col justify-between w-full flex-1",
          isTouch
            ? "h-fit"
            : "h-full w-1/2 bg-panel-middleground rounded-xl shadow-lg p-4",
        )}
      >
        <div className="flex flex-col mb-3">
          <label
            htmlFor="play-styles"
            className={cn(
              "text-dark-secondary font-semibold",
              isTouch ? "text-xs" : "text-sm",
            )}
          >
            {t("calc.bestPlayStylesPlus.title")}
          </label>
          <span className="text-dark-tertiary text-xs" id="play-styles">
            <Trans
              ns="metaRating"
              i18nKey="calc.bestPlayStylesPlus.description"
              components={{ br: <br /> }}
            />
          </span>
        </div>

        <div
          className={cn(
            "flex items-center justify-center rounded",
            isTouch
              ? "bg-panel-base py-4 gap-3"
              : "bg-panel-background p-4 gap-4",
          )}
        >
          {topPlayStylesPlus.map((ps) => (
            <PlayStyle
              hasPlus={true}
              hasNormal={false}
              large
              id={ps}
              key={ps}
            />
          ))}
        </div>
      </div>

      <div
        className={cn(
          "flex flex-col justify-between h-full w-full flex-1",
          !isTouch && "bg-panel-middleground rounded-xl shadow-lg p-4",
        )}
      >
        <div className="flex flex-col mb-3">
          <label
            htmlFor="top-stats"
            className={cn(
              "text-dark-secondary font-semibold",
              isTouch ? "text-xs" : "text-sm",
            )}
          >
            {t("calc.topStats.title")}
          </label>
          <span className="text-dark-tertiary text-xs" id="top-stats">
            {t("calc.topStats.description")}
          </span>
        </div>

        <div
          className={cn(
            "flex items-center justify-start touch-device:justify-center rounded",
            isTouch
              ? "flex-wrap bg-panel-base p-4 gap-4"
              : "flex-wrap bg-panel-background p-2 gap-2 min-h-[85px]",
          )}
        >
          {topStats.map((s, index) => (
            <div
              key={s}
              className="flex items-center justify-center text-sm font-semibold text-dark-secondary"
            >
              {playerAttributes[s]?.label ?? snakeCaseToNormalCase(s)}
              {index !== topStats.length - 1 && <div>,</div>}
            </div>
          ))}
        </div>
      </div>

      <div
        className={cn(
          "flex flex-col justify-between h-full w-full flex-1",
          !isTouch && "bg-panel-middleground rounded-xl shadow-lg p-4",
        )}
      >
        <div className="flex flex-col mb-3">
          <label
            htmlFor="accelerate"
            className={cn(
              "text-dark-secondary font-semibold",
              isTouch ? "text-xs" : "text-sm",
            )}
          >
            {t("calc.bestAccelerate.title")}
          </label>
          <span className="text-dark-tertiary text-xs" id="accelerate">
            {t("calc.bestAccelerate.description")}
          </span>
        </div>

        <div
          className={cn(
            "flex items-center justify-center rounded",
            isTouch
              ? "bg-panel-base py-4"
              : "flex-wrap bg-panel-background gap-4 p-4 min-h-[85px] justify-evenly",
          )}
        >
          <div className="flex items-center justify-between text-sm font-semibold text-dark-secondary gap-x-2">
            {topAccelerates.length > 0 ? (
              topAccelerates.map((ar, index) => (
                <div key={ar} className="flex items-cente">
                  {accelerateLabel(ar)}
                  {index !== topAccelerates.length - 1 && <div>,</div>}
                </div>
              ))
            ) : (
              <div>{t("calc.bestAccelerate.empty")}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetaRatingCalculations;
